import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { useBibtexImport } from "@/hooks/useBibtexImport";
import { useConceptList, useUpdateConcept } from "@/hooks/useConcepts";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useAddPapers, useDemotePapers, usePapers, usePromotePapers, useRemovePaper } from "@/hooks/usePapers";
import { useSeedDiscovery } from "@/hooks/useSeedDiscovery";
import { useSaveScope } from "@/hooks/useSetup";
import { SEED_TARGET_LABEL } from "@/lib/conceptGuidance";
import { trackEvent } from "@/lib/engagement";
import { candidateToPaperCreate } from "@/lib/seedPayload";
import { cleanScope, initialScope } from "@/lib/setup/fields";
import { ensureNetworks } from "@/lib/setup/ensureNetworks";
import { ensureTriage } from "@/lib/setup/ensureTriage";
import type { RefindBlame } from "@/lib/setup/refind";
import { type Bands, dragSeed, TRIAGE_CHUNK, laneCounts, lanePick, laneSummary, papersToCheck, questionHash } from "@/lib/setup/lanes";
import { LANE_PREVIEW_DEADLINE_MS, ensureLanePreview, pickAutoLanes } from "@/lib/setup/lanePreview";
import { BEST_MATCH, mergeNetworks, paperKey, previewStats, type SeedNetwork, seedReach, seedTags } from "@/lib/setup/preview";
import { type SeedCandidate, useSeedDiscoveryStore } from "@/stores/seed-discovery-store";
import { isSettled, netKey, type SeedRef, useSetupStore } from "@/stores/setup-store";
import { useZoteroBulkImportModalStore } from "@/stores/zotero-bulk-import-modal-store";
import type { Concept, ConceptScope } from "@/types/concept";
import type { PaperWithRole } from "@/types/paper";
import { toAnchorCandidate } from "./AngleStep";
import { LaneSummary } from "./LaneSummary";
import { LoadingLine } from "./LoadingLine";
import { PaperSearchBox } from "./PaperSearchBox";
import { PreviewPanel } from "./PreviewPanel";
import { RefindBanner } from "./RefindBanner";
import { type LaneState, SeedLane } from "./SeedLane";

const IMPORT_SOURCES = new Set(["bibtex_import", "user_upload_via_zotero_sync"]);

// savedId: a seed already saved on the concept; fromLibrary marks one that a
// previous visit promoted from the user's imports (unticking demotes it back).
type Card = {
  key: string; cand: SeedCandidate; savedId?: string; fromLibrary?: boolean; importedId?: string;
  /** A saved seed the current discovery run didn't (or hasn't yet) suggested — no tier to tag it with. */
  unsuggested?: boolean;
  group: "found" | "library";
};

const savedToCandidate = (p: PaperWithRole, reason: string): SeedCandidate => ({
  paper_id: p.paper_id ?? `db:${p.id}`, title: p.title, authors: p.authors, year: p.year,
  abstract: p.abstract, citation_count: p.citations, doi: p.doi, overlap_score: 0, reason,
  journal: p.journal || null,
});

const NO_BANDS: Bands = {};
/** Unticked suggestions whose lanes load without a click (Brad, 2026-11-02). */
export const AUTO_LANES = 3;

const toRef = (c: Card): SeedRef => ({
  key: c.key, paper_id: c.key.startsWith("db:") ? null : c.cand.paper_id, doi: c.cand.doi, title: c.cand.title,
});
/** "de Mink 2013": last word of the first author + year, for the drag line. */
const shortCite = (c: SeedCandidate) => [c.authors[0]?.split(" ").at(-1), c.year].filter(Boolean).join(" ");

export function SeedsStep({ concept, onDone, onSkip, refind }: {
  concept: Concept; onDone: () => void; onSkip: () => void;
  /** Set when ③ sent the user back to re-find seeds (spec 2026-10-31 §3). */
  refind?: { blame: RefindBlame | null };
}) {
  const { discover } = useSeedDiscovery(concept.id);
  const candidates = useSeedDiscoveryStore((s) => s.candidates);
  const isRunning = useSeedDiscoveryStore((s) => s.isRunning);
  const phase = useSeedDiscoveryStore((s) => s.phase);
  const error = useSeedDiscoveryStore((s) => s.error);
  const { data: me } = useCurrentUser();
  const { data: seedData } = usePapers(concept.id, { role: "foundational", mode: "all" });
  const { data: trawledData } = usePapers(concept.id, { role: "trawled", mode: "all" });
  const addPapers = useAddPapers(concept.id);
  const removePaper = useRemovePaper(concept.id);
  const promote = usePromotePapers(concept.id);
  const demote = useDemotePapers(concept.id);
  const { uploadAndImport } = useBibtexImport(concept.id);
  const anchor = useSetupStore((s) => s.anchor);
  const anchorFields = useSetupStore((s) => s.anchorFields);
  const saveScope = useSaveScope(concept.id);
  const { data: conceptList } = useConceptList();
  // Skip is for returning users: someone who has finished setup on another concept.
  const returning = !!conceptList?.some((c) => c.id !== concept.id && c.setup_completed_at);
  // Scope is local to ②; it is saved once on Next / Skip / Refresh.
  const [scope, setScope] = useState<ConceptScope>(() => concept.scope ?? initialScope(anchorFields, concept.primary_field));
  const [discoveredFor, setDiscoveredFor] = useState<ConceptScope>(scope);
  const [adding, setAdding] = useState(false);
  // Field/years drive discovery server-side; lean (recency) doesn't.
  const stale = scope.field !== discoveredFor.field || scope.year_min !== discoveredFor.year_min
    || scope.year_max !== discoveredFor.year_max;
  const recency = scope.recency;
  const [extra, setExtra] = useState<SeedCandidate[]>([]); // picked via search fallback
  const [checked, setChecked] = useState<Set<string> | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const updateConcept = useUpdateConcept(concept.id);
  const [termsError, setTermsError] = useState<string | null>(null);
  const [refound, setRefound] = useState(false);
  // The whole re-find, PATCH included: isRunning only turns true once the
  // discovery POST returns, and a second click before then 409s.
  const [finding, setFinding] = useState(false);
  // Terms last saved here: concept.keywords lags the PATCH until its refetch.
  const sentTerms = useRef<string | null>(null);
  const [searched, setSearched] = useState(() => concept.keywords ?? "");
  const bibRef = useRef<HTMLInputElement>(null);
  const ran = useRef(false);
  // A cleanup flag in the run-once effect would cancel discovery for good under
  // StrictMode (its re-run returns early on `ran`); this ref comes back on remount.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // The discovery store is global and Curate pops SeedDiscoveryDialog whenever
  // it holds candidates, so setup's candidates must not outlive this step.
  // Reset before discover() in the same effect so the order is guaranteed.
  // Discovery reads the SAVED scope server-side, so a concept without one gets
  // the default scope saved before the first run.
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    useSeedDiscoveryStore.getState().reset();
    void (async () => {
      if (concept.scope == null) {
        try {
          await saveScope.mutateAsync(cleanScope(scope));
        } catch {
          // Discovery still runs, unscoped: mark it so, so Refresh is offered.
          setDiscoveredFor({});
        }
      } else {
        // A saved scope means no await above; without this tick discover() starts
        // before StrictMode's unmount reset, which then marks the run stale and the
        // list never fills (dev only, on every resume at ②).
        await Promise.resolve();
      }
      // Unmounted mid-save: discover() would refill the store after the unmount reset.
      if (mounted.current) await discover(false, undefined, { quiet: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, [discover]);
  useEffect(() => () => useSeedDiscoveryStore.getState().reset(), []);

  const cards: Card[] = useMemo(() => {
    const out: Card[] = [];
    const seen = new Set<string>();
    const push = (c: Card) => { if (!seen.has(c.key)) { seen.add(c.key); out.push(c); } };
    if (anchor) push({ key: anchor.paper_id, cand: anchor, group: "found" });
    // A saved seed that discovery also suggested keeps discovery's tier, rank and
    // reason, so its tags don't fall back to "Recent" once it's saved.
    const found = new Map([...extra, ...(candidates ?? [])].map((c) => [c.paper_id, c]));
    for (const p of seedData?.papers ?? []) {
      const base = savedToCandidate(p, "Saved seed");
      const hit = found.get(base.paper_id);
      const cand = hit ? { ...base, tier: hit.tier, rank: hit.rank, relevance: hit.relevance, reason: hit.reason } : base;
      // Also while a run is loading (candidates null): a tierless saved seed must
      // never fall through to seedTags' "Squarely on your question".
      push({ key: cand.paper_id, cand, savedId: p.id, fromLibrary: IMPORT_SOURCES.has(p.added_source), group: "found",
        unsuggested: !hit });
    }
    for (const c of [...extra, ...(candidates ?? [])]) push({ key: c.paper_id, cand: c, group: "found" });
    // Best fit first, saved or not: saved seeds come back from the API in no
    // useful order (Frost 2024, discovery's #1, was listed 3rd). Starting paper,
    // then papers added by search, then discovery's rank, then saved seeds this
    // run didn't suggest. Array sort is stable, so ties keep the order above.
    // A fallback run has no ranks; its list order stands in.
    const rankOf = new Map((candidates ?? []).map((c, i) => [c.paper_id, c.rank ?? i + 1]));
    const order = (c: Card) => {
      if (c.key === anchor?.paper_id) return -2;
      const r = rankOf.get(c.key) ?? c.cand.rank;
      if (r != null) return r;
      return c.savedId ? Number.MAX_SAFE_INTEGER : -1;
    };
    out.sort((a, b) => order(a) - order(b));
    for (const p of trawledData?.papers ?? []) {
      if (!IMPORT_SOURCES.has(p.added_source)) continue;
      const cand = savedToCandidate(p, "From your library");
      push({ key: cand.paper_id, cand, importedId: p.id, group: "library" });
    }
    return out;
  }, [anchor, seedData, extra, candidates, trawledData]);

  // Initial selection: saved seeds + the anchor (once the saved list has loaded).
  useEffect(() => {
    if (checked !== null || !seedData) return;
    const init = new Set(useSetupStore.getState().checked.map((x) => x.key));
    if (!init.size) {
      for (const p of seedData.papers) init.add(p.paper_id ?? `db:${p.id}`);
      if (anchor) init.add(anchor.paper_id);
    }
    setChecked(init);
  }, [checked, seedData, anchor]);

  const sel = useMemo(() => checked ?? new Set<string>(), [checked]);
  const toggle = (key: string) => {
    const next = new Set(sel);
    if (next.has(key)) next.delete(key); else next.add(key);
    setChecked(next);
  };
  // Every checked seed, imported ones included: ensureNetworks resolves `db:`
  // seeds by DOI/title, so they are previewed and trawled like the rest.
  const previewSeeds: SeedRef[] = useMemo(
    () => cards.filter((c) => sel.has(c.key)).map(toRef),
    [cards, sel],
  );
  const status = useSetupStore((s) => s.status);
  // Next waits until the preview has a final answer for every checked seed
  // (retries included), so nothing is silently missing from ④'s sample.
  const previewPending = previewSeeds.some((s) => !isSettled(status[netKey(s.key, recency)]));

  async function save(): Promise<boolean> {
    setSaving(true);
    setSaveError(null);
    try {
      await saveScope.mutateAsync(cleanScope(scope));
      const chosen = cards.filter((c) => sel.has(c.key));
      const toAdd = chosen.filter((c) => !c.savedId && !c.importedId).map((c) => candidateToPaperCreate(c.cand));
      if (toAdd.length) await addPapers.mutateAsync({ papers: toAdd, role: "foundational", added_source: "seed_discovery" });
      const toPromote = chosen.filter((c) => c.importedId).map((c) => c.importedId!);
      if (toPromote.length) await promote.mutateAsync(toPromote);
      const unticked = cards.filter((c) => c.savedId && !sel.has(c.key));
      // A library paper goes back to the library; only setup's own adds are removed.
      const toDemote = unticked.filter((c) => c.fromLibrary).map((c) => c.savedId!);
      if (toDemote.length) await demote.mutateAsync(toDemote);
      for (const c of unticked) if (!c.fromLibrary) await removePaper.mutateAsync(c.savedId!);
      useSetupStore.getState().setChecked(previewSeeds);
      return true;
    } catch {
      setSaveError("Couldn't save your seeds. Try again.");
      return false;
    } finally {
      setSaving(false);
    }
  }
  const next = async () => { if (await save()) onDone(); };
  const skip = async () => { if (await save()) onSkip(); };

  // Keep ticked, not-yet-saved suggestions listed when the candidate list is replaced.
  function keepTicked() {
    const keep = cards.filter((c) => sel.has(c.key) && !c.savedId && !c.importedId && c.group === "found").map((c) => c.cand);
    setExtra((xs) => [...keep, ...xs]);
  }

  async function refresh() {
    keepTicked();
    try {
      await saveScope.mutateAsync(cleanScope(scope));
    } catch {
      setSaveError("Couldn't save the scope. Try again.");
      return;
    }
    setDiscoveredFor(scope);
    trackEvent("setup_seeds_refreshed", { concept_id: concept.id, flow: "boards" });
    await discover(true, undefined, { quiet: true });
  }

  // Re-find (spec 2026-10-31 §3). Search terms are part of the discovery
  // fingerprint, so after the PATCH the saved set is stale and a plain
  // discover() runs fresh. The banner only offers new terms (the same terms
  // find the same seeds); `searched` moves only when a run completes, so a
  // failed run can be retried — without re-saving, and still fresh (a failed
  // run saves no set under the new fingerprint).
  async function findAgain(terms: string) {
    setTermsError(null);
    const next = terms.trim();
    const changed = next !== (sentTerms.current ?? concept.keywords ?? "").trim();
    setFinding(true);
    try {
      if (changed) {
        try {
          await updateConcept.mutateAsync({ keywords: next });
        } catch {
          setTermsError("Couldn't save the search terms. Try again.");
          return;
        }
        sentTerms.current = next;
      }
      keepTicked();
      trackEvent("setup_refind_run", { concept_id: concept.id, changed_terms: changed, flow: "boards" });
      setRefound(false);
      await discover(false, undefined, { quiet: true });
      // discover() swallows failures: label only a run that completed.
      if (mounted.current && useSeedDiscoveryStore.getState().phase === "complete") { setRefound(true); setSearched(next); }
    } finally {
      if (mounted.current) setFinding(false);
    }
  }

  // Each seed's network, keyed by seed key (lanes + reach).
  const networks = useSetupStore((s) => s.networks);
  const resolved = useSetupStore((s) => s.resolved);
  const netsByKey = useMemo(() => {
    const out: Record<string, SeedNetwork> = {};
    for (const c of cards) {
      const id = resolved[c.key];
      const n = id ? networks[netKey(id, recency)] : undefined;
      if (n) out[c.key] = n;
    }
    return out;
  }, [cards, networks, resolved, recency]);
  const reach = useMemo(() => seedReach(netsByKey, [...sel], scope), [netsByKey, sel, scope]);

  // Lanes (board C), ticked first: ticked seeds, AUTO_LANES unticked
  // suggestions, any the user opened, and any network already loaded. The rest
  // wait for "Show papers": a lane per suggestion tripled ②'s first S2 burst
  // (prod 2026-11-01: 33 calls, ~45s). Which AUTO_LANES: the lane preview's
  // strongest (selector's #1-3 got 62 of a possible 112 on-question papers on
  // one concept; the preview got 100 — 2026-11-02), or the selector's order if
  // the preview fails or misses LANE_PREVIEW_DEADLINE_MS. Library imports only
  // when ticked (a big .bib must not fan out into hundreds of fetches).
  const [opened, setOpened] = useState<Set<string>>(() => new Set());
  const qh = useMemo(() => questionHash(concept.abstract ?? ""), [concept.abstract]);
  const unticked = useMemo(() => cards.filter((c) => !sel.has(c.key) && c.group === "found"), [cards, sel]);
  const suggestions = useMemo(() => cards.filter((c) => c.group === "found" && !c.savedId && !c.unsuggested), [cards]);
  const preview = useSetupStore((s) => s.lanePreview[qh]);
  const suggestionSig = suggestions.map((c) => c.key).join(",");
  useEffect(() => {
    if (suggestions.length) {
      void ensureLanePreview(concept.id, qh, suggestions.map((c) => ({ key: c.key, s2Id: c.key, doi: c.cand.doi ?? null })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the suggestion ids, not the array
  }, [suggestionSig, concept.id, qh]);
  const [previewLate, setPreviewLate] = useState(false);
  const previewReady = suggestions.every((c) => preview && c.key in preview);
  useEffect(() => {
    if (previewReady || !suggestions.length) return;
    const t = setTimeout(() => setPreviewLate(true), LANE_PREVIEW_DEADLINE_MS);
    return () => clearTimeout(t);
  }, [previewReady, suggestions.length]);
  // The ORDER is fixed once per suggestion set (a late preview never reshuffles
  // lanes already loading); the auto lanes are the first AUTO_LANES unticked in
  // it, so ticking one pulls in the next (Brad, 2026-11-02).
  const [auto, setAuto] = useState<{ from: string; order: string[] } | null>(null);
  useEffect(() => {
    if (auto?.from === suggestionSig || !suggestions.length) return;
    if (!previewReady && !previewLate) return;
    const found = cards.filter((c) => c.group === "found").map((c) => c.key);
    const order = previewReady ? pickAutoLanes(found, preview ?? {}, found.length) : found;
    setAuto({ from: suggestionSig, order });
    const untickedOrder = unticked.map((c) => c.key);
    trackEvent("setup_lane_preview", {
      concept_id: concept.id, by_preview: previewReady,
      picked: order.filter((k) => untickedOrder.includes(k)).slice(0, AUTO_LANES)
        .map((k) => untickedOrder.indexOf(k) + 1).join(","),
    });
  }, [auto, suggestionSig, suggestions.length, previewReady, previewLate, cards, unticked, preview, concept.id]);
  const autoPicked = auto?.from === suggestionSig || !suggestions.length;
  // Staged loading: with nothing ticked, the selector's #1 starts its lane while
  // the preview runs, so one lane fills ~7s sooner (prod 2026-11-03: preview
  // 7.4s, then 5.6-7.5s of network fetch before triage). It stays a lane even
  // if the preview ranks it low (never pull a lane the user is looking at), so
  // there can be AUTO_LANES + 1. Not on a revisit with the preview stored.
  const [head, setHead] = useState<{ from: string; key: string } | null>(null);
  useEffect(() => {
    if (previewReady || head?.from === suggestionSig || sel.size || !unticked.length) return;
    setHead({ from: suggestionSig, key: unticked[0].key });
  }, [previewReady, head, suggestionSig, sel.size, unticked]);
  const headKey = head?.from === suggestionSig ? head.key : null;
  const laneCards = useMemo(() => {
    const untickedKeys = new Set(unticked.map((c) => c.key));
    const autoKeys = new Set((auto?.order ?? []).filter((k) => untickedKeys.has(k)).slice(0, AUTO_LANES));
    return [...cards.filter((c) => sel.has(c.key)),
      ...unticked.filter((c) => c.key === headKey || autoKeys.has(c.key) || opened.has(c.key) || !!netsByKey[c.key])];
  }, [cards, sel, unticked, auto, headKey, opened, netsByKey]);
  const laneKeys = useMemo(() => new Set(laneCards.map((c) => c.key)), [laneCards]);
  const openLane = (key: string) => {
    setOpened((o) => new Set(o).add(key));
    trackEvent("setup_lane_opened", { concept_id: concept.id });
  };
  const laneSeeds = useMemo(() => laneCards.map(toRef), [laneCards]);
  useEffect(() => { void ensureNetworks(laneSeeds, recency); }, [laneSeeds, recency]);

  const bands = useSetupStore((s) => s.triage[qh]) ?? NO_BANDS;
  const triageError = useSetupStore((s) => s.triageError[qh]);
  const picks = useMemo(
    () => Object.fromEntries(Object.entries(netsByKey).map(([k, n]) => [k, lanePick(n, scope)])),
    [netsByKey, scope],
  );
  useEffect(() => {
    const order = laneSeeds.map((s) => picks[s.key]).filter((p) => !!p);
    const todo = papersToCheck(order, useSetupStore.getState().triage[qh] ?? NO_BANDS);
    // Each request costs one daily slot, and networks land a seed at a time:
    // while any are still loading, send only full chunks; the rest goes once
    // every lane's network has settled. Auto lanes not picked yet counts as
    // loading too, except for the staged lane: checked on its own once its
    // network settles, one slot buys it ~7s.
    const loading = (!autoPicked && !headKey) || laneSeeds.some((s) => !isSettled(status[netKey(s.key, recency)]));
    const send = loading ? todo.slice(0, todo.length - (todo.length % TRIAGE_CHUNK)) : todo;
    if (send.length) void ensureTriage(concept.id, qh, send);
  }, [laneSeeds, picks, qh, concept.id, status, recency, autoPicked, headKey]);
  const retryTriage = () => {
    const order = laneSeeds.map((s) => picks[s.key]).filter((p) => !!p);
    void ensureTriage(concept.id, qh, papersToCheck(order, bands), { retry: true });
  };

  const tickedKeys = useMemo(() => [...sel].filter((k) => picks[k]), [sel, picks]);
  const total = useMemo(
    () => previewStats(mergeNetworks(tickedKeys.map((k) => netsByKey[k])), scope).inRange,
    [tickedKeys, netsByKey, scope],
  );
  const summary = laneSummary(tickedKeys.map((k) => picks[k]), bands, total);
  // Only a Background seed is offered for unticking: on a narrow question every
  // seed is mostly off it, and the seed about the subject itself (often the most
  // cited) must never be the one we suggest dropping. Saved seeds lose their tier
  // on the card, so it is looked up from discovery's candidates.
  const coreKeys = useMemo(
    () => new Set([...(candidates ?? []), ...extra].filter((c) => c.tier === "core").map((c) => c.paper_id)),
    [candidates, extra],
  );
  const dragRow = sel.size > 1 ? dragSeed(tickedKeys.filter((k) => !coreKeys.has(k) && k !== anchor?.paper_id).map((k) => {
    const n = reach[k]?.unique ?? 0;
    const lc = laneCounts(picks[k], bands, n);
    return { key: k, n, on: lc.onEstimate, share: lc.share, checked: lc.checked };
  })) : null;
  const dragCard = dragRow ? cards.find((c) => c.key === dragRow.key) : undefined;
  const untickDrag = () => {
    if (!dragRow) return;
    toggle(dragRow.key);
    trackEvent("setup_drag_untick", { concept_id: concept.id });
  };
  const blame = refind?.blame ?? null;
  const blameCard = blame ? cards.find((c) => c.key === blame.key) : undefined;
  // One suggestion per seed: the drag line wins when it names the same one.
  const canUntickBlamed = !!blame && sel.has(blame.key) && dragRow?.key !== blame.key;
  const lanesShown = useRef(false);
  const checkedCount = Object.values(bands).filter((b) => b != null).length;
  useEffect(() => {
    if (lanesShown.current || !checkedCount) return;
    lanesShown.current = true;
    trackEvent("setup_lanes_shown", { concept_id: concept.id, seeds: laneSeeds.length, checked: checkedCount });
  }, [checkedCount, concept.id, laneSeeds.length]);

  const laneFor = (c: Card) => {
    if (c.group === "library" && !sel.has(c.key)) return null;
    if (!laneKeys.has(c.key)) return { state: (autoPicked ? "idle" : "checking") as LaneState, dots: [], counts: null };
    const pick = picks[c.key];
    const st = status[netKey(c.key, recency)];
    if (!pick) return st === "empty" || st === "failed" ? null : { state: "loading" as LaneState, dots: [], counts: null };
    const dots = pick.flatMap((x) => {
      const band = bands[paperKey(x.paper)];
      return band == null ? [] : [{ key: paperKey(x.paper), band }];
    });
    const pending = pick.some((x) => !(paperKey(x.paper) in bands));
    const state: LaneState = dots.length || !pending ? "ready" : triageError ?? "loading";
    return { state, dots, counts: laneCounts(pick, bands, reach[c.key]?.unique ?? 0) };
  };
  const nowYear = new Date().getFullYear();

  const empty = !isRunning && candidates !== null && candidates.length === 0;
  const remaining = me?.plan_limits.scoring_per_concept ?? null;

  const renderCard = (c: Card) => {
    const r = reach[c.key];
    const tags = c.key === anchor?.paper_id ? ["Your starting paper"]
      : c.group === "library" ? ["From your library"]
      // Without a tier, seedTags' old fallback would call it "Squarely on your
      // question" — wrong for a Background seed a re-find dropped.
      : c.unsuggested ? [c.fromLibrary ? "From your library" : "Picked earlier"]
      : seedTags({ ...c.cand, journal: c.cand.journal ?? null }, nowYear);
    const lane = laneFor(c);
    const note = r && r.outOfRange > r.inRange ? "mostly outside your years" : null;
    return (
      <li key={c.key} className={cn("flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border p-3",
        sel.has(c.key) ? "border-primary bg-primary/5" : "bg-background")}>
        <div className="flex min-w-0 flex-[999_1_320px] gap-3">
          <Checkbox id={`seed-${c.key}`} checked={sel.has(c.key)} onCheckedChange={() => toggle(c.key)}
            aria-label={c.cand.title} className="mt-1" />
          <label htmlFor={`seed-${c.key}`} className="flex min-w-0 flex-1 flex-col gap-1">
            <span className={cn("font-medium", !sel.has(c.key) && "text-foreground/85")}>{c.cand.title}</span>
            <span className="text-sm text-muted-foreground">
              {c.cand.authors.slice(0, 3).join(", ")}{c.cand.year ? ` · ${c.cand.year}` : ""}{c.cand.journal ? ` · ${c.cand.journal}` : ""}
            </span>
            <span className="flex flex-wrap items-center gap-2 text-sm">
              {tags.map((t, i) => (
                <span key={t} title={i === 0 ? c.cand.reason || undefined : undefined}
                  className={cn("rounded px-2 py-0.5",
                    t === BEST_MATCH ? "bg-primary/15 font-medium text-primary"
                      : i === 0 ? "bg-muted" : "border text-muted-foreground")}>{t}</span>
              ))}
            </span>
          </label>
        </div>
        {lane && (
          <SeedLane state={lane.state} dots={lane.dots} reach={r ? r.unique : null}
            onEstimate={lane.counts?.onEstimate ?? 0} share={lane.counts?.share ?? 0}
            note={note} onRetry={retryTriage} onShow={() => openLane(c.key)} />
        )}
      </li>
    );
  };

  const ticked = sel.size;
  const disabled = ticked === 0 || saving || previewPending;
  return (
    <div className="grid gap-8 md:grid-cols-[minmax(0,1fr)_380px]">
      <section className="flex flex-col gap-5">
        <div>
          <h1 className="text-2xl font-semibold">Pick the papers to grow from</h1>
          <p className="mt-1 text-muted-foreground">We follow what your seeds cite and what cites them. Each strip shows how those papers fit your question.</p>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="font-medium">Suggested seeds · <span className="font-normal text-muted-foreground">{ticked} of {SEED_TARGET_LABEL} ticked</span>
            {refound && !isRunning && <span className="font-normal text-muted-foreground"> · re-found with new search terms</span>}</span>
          <Button variant="outline" size="sm" onClick={() => setAdding((a) => !a)}>Add a paper you know</Button>
        </div>
        {(adding || empty || error) && (
          <PaperSearchBox label="Add a seed by title or DOI" onPick={(p) => {
            const c = toAnchorCandidate(p);
            setExtra((xs) => [c, ...xs]);
            setChecked(new Set([...sel, c.paper_id]));
            setAdding(false);
          }} />
        )}
        {refind && (
          <RefindBanner
            terms={concept.keywords ?? ""}
            searched={searched}
            busy={finding || isRunning}
            error={termsError}
            blame={blame && { label: (blameCard && shortCite(blameCard.cand)) || blame.label, offTopic: blame.offTopic, total: blame.total }}
            onFind={(t) => void findAgain(t)}
            onUntick={canUntickBlamed ? () => toggle(blame!.key) : null}
          />
        )}
        {stale && (
          <p className="text-sm">
            Suggestions were found for your old field and years —{" "}
            <Button variant="link" className="h-auto p-0" onClick={refresh} disabled={isRunning}>Refresh suggestions</Button>
          </p>
        )}
        {(isRunning || (candidates === null && !error)) && (
          <LoadingLine>Finding seed papers…{isRunning && phase ? ` (${phase})` : ""}</LoadingLine>
        )}
        {error && <p role="alert" className="text-destructive">Discovery didn't work this time: {error}</p>}
        {empty && !error && <p>No suggestions to show. Add a paper you know, or bring in papers you already have.</p>}
        {/* Shown from the first strip on (a placeholder until a tick), so ticking
            never pushes the list down under the pointer. */}
        {(summary || checkedCount > 0) && (
          <LaneSummary ticked={tickedKeys.length} summary={summary}
            drag={dragRow && dragCard ? { label: shortCite(dragCard.cand), n: dragRow.n, on: dragRow.on } : null}
            onUntick={untickDrag} problem={triageError} onRetry={retryTriage} />
        )}
        <ul className="flex flex-col gap-2">{cards.filter((c) => c.group === "found").map(renderCard)}</ul>
        {cards.some((c) => c.group === "library") && (
          <div className="flex flex-col gap-2">
            <h2 className="font-medium">From your library</h2>
            <ul className="flex flex-col gap-2">{cards.filter((c) => c.group === "library").map(renderCard)}</ul>
          </div>
        )}
        <p className="text-sm text-muted-foreground">
          Already have papers?{" "}
          <Button variant="link" className="h-auto p-0" onClick={() => bibRef.current?.click()}>Import a .bib file</Button>{" · "}
          <Button variant="link" className="h-auto p-0" onClick={() => useZoteroBulkImportModalStore.getState().open(concept.id)}>From Zotero</Button>
        </p>
        <input ref={bibRef} type="file" accept=".bib" className="hidden" onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void uploadAndImport(f, "trawled");
          e.target.value = "";
        }} />
      </section>
      <PreviewPanel seeds={previewSeeds} scope={scope} onScope={setScope} remaining={remaining}>
        <div className="flex flex-col gap-2 border-t pt-4">
          <span className="text-sm text-muted-foreground">Next: grade 8 of these as a test</span>
          <Button disabled={disabled} onClick={next}>{saving ? "Saving…" : previewPending ? "Waiting for the preview…" : "Check the grading →"}</Button>
          {returning && (
            <Button variant="link" className="h-auto w-fit p-0 text-muted-foreground" disabled={disabled} onClick={skip}>
              Skip the test and start
            </Button>
          )}
          {saveError && <p role="alert" className="text-destructive">{saveError}</p>}
        </div>
      </PreviewPanel>
    </div>
  );
}
